import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, ChevronLeft, ChevronRight, Check, Shield, FileText, Building2, Search, ShoppingBag } from 'lucide-react';

const iconMap = { Shield, FileText, Building2, Search, ShoppingBag };

const slides = [
  {
    icon: 'Shield',
    title: 'Trademark Registration',
    description: 'File your federal trademark application with the USPTO and secure exclusive legal protection for your brand name, logo, or slogan across all 50 states.',
    features: ['Online trademark filing with attorney support', 'Full USPTO application and processing', 'Federal protection nationwide', 'Dedicated case management'],
    path: '/services/trademark-registration',
    btn: 'File a Trademark',
    bg: 'https://images.unsplash.com/photo-1645484686977-dbddd9e1dc0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx0cmFkZW1hcmslMjBsZWdhbCUyMGRvY3VtZW50JTIwc3RhbXAlMjBzZWFsfGVufDB8fHx8MTc3NTUwODAwNXww&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: 'FileText',
    title: 'Copyright Registration',
    description: 'Protect original creative works, digital assets, and intellectual property through professional copyright registration with the US Copyright Office.',
    features: ['Online copyright filing', 'Digital content protection', 'Attorney-reviewed applications', 'Complete ownership documentation'],
    path: '/services/copyright-registration',
    btn: 'File Copyright',
    bg: 'https://images.unsplash.com/photo-1739891625683-bbd845008ccf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjb3B5cmlnaHQlMjBzeW1ib2wlMjBkaWdpdGFsJTIwYXJ0JTIwZGFyayUyMGFic3RyYWN0fGVufDB8fHx8MTc3NTUwODc3Nnww&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: 'Building2',
    title: 'Company Formation',
    description: 'Form your LLC or corporation with proper legal structure. We handle state filings, EIN registration, and operating agreements from start to finish.',
    features: ['LLC and corporation formation', 'State filing and compliance', 'EIN registration included', 'Operating agreement drafting'],
    path: '/services/company-formation',
    btn: 'Start Your LLC',
    bg: 'https://images.unsplash.com/photo-1714766109677-86d817364ab8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxkYXJrJTIwb2ZmaWNlJTIwY29ycG9yYXRlJTIwYnVzaW5lc3MlMjBkb2N1bWVudHMlMjBkZXNrJTIwbW9vZHl8ZW58MHx8fHwxNzc1NTA4Nzg1fDA&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: 'Search',
    title: 'Trademark Search',
    description: 'Deep search across USPTO records, state databases, and common-law sources. Know exactly where you stand before spending money on a filing.',
    features: ['USPTO database search', 'State and common law coverage', 'Conflict risk analysis', 'Expert filing recommendations'],
    path: '/services/trademark-search',
    btn: 'Run a Search',
    bg: 'https://images.unsplash.com/photo-1609877991923-98a059340145?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtYWduaWZ5aW5nJTIwZ2xhc3MlMjBkb2N1bWVudCUyMGRhcmslMjBtb29keSUyMHNlYXJjaHxlbnwwfHx8fDE3NzU1MDg3ODV8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: 'ShoppingBag',
    title: 'Amazon Brand Registry',
    description: 'Enroll in Amazon Brand Registry with a qualifying trademark. Unlock A+ Content, Sponsored Brands, and IP tools that protect and grow your Amazon business.',
    features: ['Brand Registry enrollment', 'Trademark filing for Amazon', 'Amazon IP protection setup', 'Enhanced listing tools access'],
    path: '/services/amazon-brand-registry',
    btn: 'Enroll in Brand Registry',
    bg: 'https://images.unsplash.com/photo-1656232074732-8468f897188e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBkYXJrJTIwYm94ZXMlMjBkZWxpdmVyeSUyMHBhY2thZ2V8ZW58MHx8fHwxNzc1NTA4Nzk3fDA&ixlib=rb-4.1.0&q=85',
  },
];

const OurServicesSection = ({ onOpenContactForm }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const next = useCallback(() => setCurrent((p) => (p + 1) % slides.length), []);
  const prev = useCallback(() => setCurrent((p) => (p - 1 + slides.length) % slides.length), []);

  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [isPaused, next]);

  const s = slides[current];
  const Icon = iconMap[s.icon];

  return (
    <section
      id="services"
      className="relative overflow-hidden"
      data-testid="services-section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background images - all preloaded, only active one visible */}
      {slides.map((slide, i) => (
        <div
          key={slide.title}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: i === current ? 1 : 0, zIndex: 0 }}
        >
          <img
            src={slide.bg}
            alt=""
            className="w-full h-full object-cover"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
          <div className="absolute inset-0 bg-[#000a24]/[0.88]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#000a24]/40 via-transparent to-[#000a24]/50" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-16">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-blue-300/80 mb-3">Our Services</p>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-16"
            style={{ fontFamily: 'Outfit,sans-serif' }}
          >
            Everything You Need to Protect Your Brand
          </h2>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[400px]">
            {/* Left: Slide content */}
            <div
              key={current}
              className="animate-fade-slide-in"
            >
              <div className="w-14 h-14 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl flex items-center justify-center mb-6">
                {Icon && <Icon className="w-7 h-7 text-white" />}
              </div>
              <h3
                className="text-xl sm:text-2xl font-bold text-white mb-4"
                style={{ fontFamily: 'Outfit,sans-serif' }}
                data-testid="service-slide-title"
              >
                {s.title}
              </h3>
              <p className="text-blue-100/80 leading-relaxed mb-8 text-sm max-w-xl">
                {s.description}
              </p>

              <div className="space-y-3 mb-8">
                {s.features.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-blue-300" />
                    </div>
                    <span className="text-sm text-blue-100/70">{f}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate(s.path)}
                className="bg-white hover:bg-zinc-100 text-[#002FA7] font-semibold px-8 py-5 rounded-md transition-all duration-300 hover:-translate-y-0.5"
                data-testid={`service-btn-${s.path.split('/').pop()}`}
              >
                {s.btn} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* Right: Slide selector cards */}
            <div className="space-y-3">
              {slides.map((slide, i) => {
                const SIcon = iconMap[slide.icon];
                const isActive = i === current;
                return (
                  <button
                    key={slide.title}
                    onClick={() => setCurrent(i)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-300 flex items-center gap-4 group ${
                      isActive
                        ? 'bg-white/15 backdrop-blur-sm border border-white/25'
                        : 'bg-white/5 border border-transparent hover:bg-white/10 hover:border-white/15'
                    }`}
                    data-testid={`service-card-${slide.path.split('/').pop()}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                      isActive ? 'bg-white/20' : 'bg-white/10 group-hover:bg-white/15'
                    }`}>
                      {SIcon && <SIcon className="w-5 h-5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold text-sm transition-colors duration-300 ${
                        isActive ? 'text-white' : 'text-blue-100/70 group-hover:text-white'
                      }`} style={{ fontFamily: 'Outfit,sans-serif' }}>
                        {slide.title}
                      </h4>
                      {isActive && (
                        <p className="text-xs text-blue-200/50 mt-1 line-clamp-1">{slide.description}</p>
                      )}
                    </div>
                    <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${
                      isActive ? 'text-white translate-x-0' : 'text-white/30 -translate-x-1 group-hover:text-white/60 group-hover:translate-x-0'
                    }`} />

                    {/* Progress bar for active slide */}
                    {isActive && !isPaused && (
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-white/40 rounded-full animate-progress-bar" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bottom nav: arrows + dots */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-white/10">
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === current ? 'w-8 bg-white' : 'w-4 bg-white/25 hover:bg-white/40'
                  }`}
                  data-testid={`service-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all duration-300"
                data-testid="service-prev-btn"
                aria-label="Previous service"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all duration-300"
                data-testid="service-next-btn"
                aria-label="Next service"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurServicesSection;
